import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useRef, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useInvalidateCoachProfile, getInitials } from "@/hooks/useCoachProfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { GoogleCalendarCard } from "@/components/settings/GoogleCalendarCard";
import { PasswordCard } from "@/components/settings/PasswordCard";
import { Camera, CloudSun, HardDrive, MailQuestion, Receipt, UserRound } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/settings/")({
  head: () => ({
    meta: buildAppMeta({
      title: "Settings — CoachQuick",
      description:
        "Account, profile, calendar, email, and integration settings for your CoachQuick workspace.",
      path: "/app/settings",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/settings` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <SettingsPage />
    </RequireAuth>
  ),
});

type Profile = {
  full_name: string;
  title: string;
  phone: string;
  website: string;
  sign_off: string;
  avatar_url: string;
  reengagement_enabled: boolean;
  reengagement_threshold_days: number;
  business_name: string;
  business_address: string;
  tax_id: string;
  invoice_number_prefix: string;
  default_payment_instructions: string;
  invoice_footer: string;
  weather_enabled: boolean;
  weather_default_sport: "pickleball" | "tennis" | "golf" | "generic";
  weather_indoor_severe_alerts: boolean;
  weather_send_3day_digest: boolean;
  weather_send_day_before: boolean;
  weather_threshold_precip_prob: number;
  weather_threshold_precip_mm: number;
  weather_threshold_wind_mph: number;
  weather_threshold_snow_cm: number;
  weather_threshold_temp_min_f: number;
  weather_threshold_temp_max_f: number;
};

const EMPTY: Profile = {
  full_name: "",
  title: "",
  phone: "",
  website: "",
  sign_off: "Thanks!",
  avatar_url: "",
  reengagement_enabled: true,
  reengagement_threshold_days: 14,
  business_name: "",
  business_address: "",
  tax_id: "",
  invoice_number_prefix: "INV",
  default_payment_instructions: "",
  invoice_footer: "",
  weather_enabled: true,
  weather_default_sport: "pickleball",
  weather_indoor_severe_alerts: true,
  weather_send_3day_digest: true,
  weather_send_day_before: true,
  weather_threshold_precip_prob: 50,
  weather_threshold_precip_mm: 0.5,
  weather_threshold_wind_mph: 15,
  weather_threshold_snow_cm: 1.0,
  weather_threshold_temp_min_f: 35,
  weather_threshold_temp_max_f: 100,
};

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "password", label: "Password" },
  { id: "calendar", label: "Google Calendar" },
  { id: "nudges", label: "Re-engagement nudges" },
  { id: "invoicing", label: "Invoicing" },
  { id: "weather", label: "Weather alerts" },
  { id: "storage", label: "Clip storage" },
];

function SettingsPage() {
  const { user } = useAuth();
  const invalidateProfile = useInvalidateCoachProfile();
  const [p, setP] = useState<Profile>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("profile");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("coach_profile" as never)
        .select("*")
        .eq("coach_id", user.id)
        .maybeSingle();
      if (data) {
        const d = data as unknown as Partial<Profile>;
        setP({
          full_name: d.full_name ?? (user.user_metadata?.full_name as string) ?? "",
          title: d.title ?? "",
          phone: d.phone ?? "",
          website: d.website ?? "",
          sign_off: d.sign_off ?? "Thanks!",
          avatar_url: d.avatar_url ?? "",
          reengagement_enabled: d.reengagement_enabled ?? true,
          reengagement_threshold_days: d.reengagement_threshold_days ?? 14,
          business_name: d.business_name ?? "",
          business_address: d.business_address ?? "",
          tax_id: d.tax_id ?? "",
          invoice_number_prefix: d.invoice_number_prefix ?? "INV",
          default_payment_instructions: d.default_payment_instructions ?? "",
          invoice_footer: d.invoice_footer ?? "",
          weather_enabled: d.weather_enabled ?? true,
          weather_default_sport: d.weather_default_sport ?? "pickleball",
          weather_indoor_severe_alerts: d.weather_indoor_severe_alerts ?? true,
          weather_send_3day_digest: d.weather_send_3day_digest ?? true,
          weather_send_day_before: d.weather_send_day_before ?? true,
          weather_threshold_precip_prob: Number(d.weather_threshold_precip_prob ?? 50),
          weather_threshold_precip_mm: Number(d.weather_threshold_precip_mm ?? 0.5),
          weather_threshold_wind_mph: Number(d.weather_threshold_wind_mph ?? 15),
          weather_threshold_snow_cm: Number(d.weather_threshold_snow_cm ?? 1.0),
          weather_threshold_temp_min_f: Number(d.weather_threshold_temp_min_f ?? 35),
          weather_threshold_temp_max_f: Number(d.weather_threshold_temp_max_f ?? 100),
        });
      } else {
        setP({ ...EMPTY, full_name: user.user_metadata?.full_name ?? "" });
      }
      setLoading(false);
    })();
  }, [user]);

  // Highlight the section nearest the top of the viewport as you scroll.
  useEffect(() => {
    if (loading) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -60% 0px" },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [loading]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from("coach_profile" as never)
        .upsert({ coach_id: user.id, ...p } as never, { onConflict: "coach_id" } as never);
      if (error) throw error;
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleAvatarFile(file: File) {
    if (!user) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("coach-avatars")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("coach-avatars").getPublicUrl(path);
      const url = pub.publicUrl;
      const { error: dbErr } = await supabase
        .from("coach_profile" as never)
        .upsert(
          { coach_id: user.id, avatar_url: url } as never,
          { onConflict: "coach_id" } as never,
        );
      if (dbErr) throw dbErr;
      setP((prev) => ({ ...prev, avatar_url: url }));
      invalidateProfile();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function removeAvatar() {
    if (!user) return;
    setUploading(true);
    try {
      const { error } = await supabase
        .from("coach_profile" as never)
        .upsert(
          { coach_id: user.id, avatar_url: null } as never,
          { onConflict: "coach_id" } as never,
        );
      if (error) throw error;
      setP((prev) => ({ ...prev, avatar_url: "" }));
      invalidateProfile();
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setUploading(false);
    }
  }

  const avatarInitials = getInitials(p.full_name, user?.email);
  const identityLine = [p.title, user?.email].filter(Boolean).join(" · ");

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        <h1 className="font-display text-3xl font-black tracking-tight text-foreground md:text-4xl">
          Settings
        </h1>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleAvatarFile(f);
          }}
        />

        {/* Account hero */}
        <section className="mt-6 flex flex-wrap items-center gap-5 rounded-[20px] bg-ink-panel px-6 py-6 text-white sm:px-7">
          <div className="relative shrink-0 leading-none">
            <Avatar className="h-[72px] w-[72px]">
              {p.avatar_url ? (
                <AvatarImage src={p.avatar_url} alt="Your photo" className="object-cover" />
              ) : null}
              <AvatarFallback className="bg-primary text-xl font-semibold text-primary-foreground">
                {avatarInitials}
              </AvatarFallback>
            </Avatar>
            {p.avatar_url && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Change your photo"
                    className="absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-ink-panel bg-primary text-primary-foreground"
                  >
                    <Camera className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }}
                  >
                    Change photo
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => removeAvatar()}
                  >
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="min-w-[200px]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
              Your account
            </p>
            <p className="mt-2 font-display text-[26px] font-black leading-tight tracking-[-0.02em]">
              {p.full_name || "Your name"}
            </p>
            {identityLine && <p className="mt-1.5 text-sm text-white/70">{identityLine}</p>}
          </div>
          <div className="ml-auto flex flex-col items-start gap-2">
            {!p.avatar_url && (
              <Button size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                {uploading ? "Uploading…" : "Upload photo"}
              </Button>
            )}
            <p className="max-w-[220px] text-xs text-white/55">
              PNG or JPG, up to 5MB. Shown in the header and on coach emails.
            </p>
          </div>
        </section>

        {loading ? (
          <div className="mt-6 rounded-[20px] border bg-card p-5 text-sm text-muted-foreground">
            Loading…
          </div>
        ) : (
          <div className="mt-6 grid gap-8 md:grid-cols-[200px_minmax(0,1fr)] md:items-start">
            {/* Sticky section nav */}
            <nav
              className="sticky top-[88px] hidden flex-col gap-0.5 md:flex"
              aria-label="Settings sections"
            >
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={cn(
                    "rounded-[10px] px-3 py-2 text-sm font-medium transition",
                    active === s.id
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s.label}
                </a>
              ))}
            </nav>

            {/* Sections */}
            <div className="flex min-w-0 flex-col gap-5">
              <SettingsSection
                id="profile"
                icon={UserRound}
                title="Profile"
                description="Your sign-off block — auto-appended to every recap email."
              >
                <form onSubmit={save} className="grid max-w-xl gap-4">
                  <div>
                    <Label htmlFor="full_name">Full name</Label>
                    <Input
                      id="full_name"
                      value={p.full_name}
                      onChange={(e) => setP({ ...p, full_name: e.target.value })}
                      placeholder="Ted Banucci"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Title / certification</Label>
                    <Input
                      id="title"
                      value={p.title}
                      onChange={(e) => setP({ ...p, title: e.target.value })}
                      placeholder="PCI Certified Pickleball Instructor"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={p.phone}
                        onChange={(e) => setP({ ...p, phone: e.target.value })}
                        placeholder="(408) 555-1234"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        value={p.website}
                        onChange={(e) => setP({ ...p, website: e.target.value })}
                        placeholder="https://yoursite.com"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="sign_off">Sign-off line</Label>
                    <Textarea
                      id="sign_off"
                      value={p.sign_off}
                      onChange={(e) => setP({ ...p, sign_off: e.target.value })}
                      rows={2}
                      placeholder="Thanks!"
                      className="mt-1.5"
                    />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full sm:w-auto">
                    {busy ? "Saving…" : "Save profile"}
                  </Button>
                </form>
              </SettingsSection>

              <PasswordCard />

              <GoogleCalendarCard />

              <SettingsSection
                id="nudges"
                icon={MailQuestion}
                title="Re-engagement nudges"
                description="Each Monday at 8am, we scan for clients you haven't seen recently and draft personalized check-in emails for your review."
              >
                <div className="grid max-w-xl gap-4">
                  <div className="flex items-center justify-between gap-4 rounded-[12px] border p-3.5">
                    <div>
                      <p className="text-sm font-medium">Enable weekly nudges</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Drafts only — nothing sends without your approval.
                      </p>
                    </div>
                    <Switch
                      checked={p.reengagement_enabled}
                      onCheckedChange={(v) => setP({ ...p, reengagement_enabled: v })}
                    />
                  </div>
                  <div className="max-w-[180px]">
                    <Label htmlFor="threshold">Stale threshold (days)</Label>
                    <Input
                      id="threshold"
                      type="number"
                      min={1}
                      max={365}
                      value={p.reengagement_threshold_days}
                      onChange={(e) =>
                        setP({ ...p, reengagement_threshold_days: parseInt(e.target.value) || 14 })
                      }
                      className="mt-1.5"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      A client is considered stale after this many days without a sent lesson.
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Send time: Mondays at 8am UTC (fixed for now). Per-client opt-out is available
                    in each client's profile.
                  </p>
                  <Button onClick={save} disabled={busy} className="w-full sm:w-auto">
                    {busy ? "Saving…" : "Save nudge settings"}
                  </Button>
                </div>
              </SettingsSection>

              <SettingsSection
                id="invoicing"
                icon={Receipt}
                title="Invoicing"
                description="Business info shown on invoices, plus your default payment instructions and invoice number format."
              >
                <div className="grid max-w-xl gap-4">
                  <div>
                    <Label htmlFor="business_name">Business name</Label>
                    <Input
                      id="business_name"
                      value={p.business_name}
                      onChange={(e) => setP({ ...p, business_name: e.target.value })}
                      placeholder="Taylor Pickleball Coaching"
                      className="mt-1.5"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Shown as the sender at the top of every invoice.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="business_address">Business address</Label>
                    <Textarea
                      id="business_address"
                      value={p.business_address}
                      onChange={(e) => setP({ ...p, business_address: e.target.value })}
                      rows={2}
                      placeholder="123 Court Lane&#10;San Jose, CA 95110"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="tax_id">Tax ID (optional)</Label>
                      <Input
                        id="tax_id"
                        value={p.tax_id}
                        onChange={(e) => setP({ ...p, tax_id: e.target.value })}
                        placeholder="EIN or SSN"
                        className="mt-1.5"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Stored only on your invoices for your records.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="invoice_number_prefix">Invoice number prefix</Label>
                      <Input
                        id="invoice_number_prefix"
                        value={p.invoice_number_prefix}
                        onChange={(e) =>
                          setP({
                            ...p,
                            invoice_number_prefix: e.target.value
                              .toUpperCase()
                              .replace(/[^A-Z0-9-]/g, "")
                              .slice(0, 8),
                          })
                        }
                        placeholder="INV"
                        className="mt-1.5"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Example:{" "}
                        <span className="font-mono">
                          {p.invoice_number_prefix || "INV"}-2026-0001
                        </span>
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="default_payment_instructions">
                      Default payment instructions
                    </Label>
                    <Textarea
                      id="default_payment_instructions"
                      value={p.default_payment_instructions}
                      onChange={(e) => setP({ ...p, default_payment_instructions: e.target.value })}
                      rows={3}
                      placeholder="Venmo @coach-taylor or Zelle to taylor@example.com"
                      className="mt-1.5"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Pre-filled on every new invoice. Editable per invoice.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="invoice_footer">Invoice footer</Label>
                    <Textarea
                      id="invoice_footer"
                      value={p.invoice_footer}
                      onChange={(e) => setP({ ...p, invoice_footer: e.target.value })}
                      rows={2}
                      placeholder="Thanks for your business!"
                      className="mt-1.5"
                    />
                  </div>
                  <Button onClick={save} disabled={busy} className="w-full sm:w-auto">
                    {busy ? "Saving…" : "Save invoicing settings"}
                  </Button>
                </div>
              </SettingsSection>

              <SettingsSection
                id="weather"
                icon={CloudSun}
                title="Weather alerts"
                description={
                  <>
                    Heads-up icons appear next to upcoming lessons when the forecast looks risky.
                    Manage venues on the{" "}
                    <a
                      className="text-brand-text underline underline-offset-2"
                      href="/app/locations"
                    >
                      Locations
                    </a>{" "}
                    tab.
                  </>
                }
              >
                <div className="grid max-w-xl gap-4">
                  <ToggleRow
                    title="Enable weather alerts"
                    desc="Turn off to hide all weather indicators and digests."
                    checked={p.weather_enabled}
                    onChange={(v) => setP({ ...p, weather_enabled: v })}
                  />
                  <ToggleRow
                    title="Severe-weather alerts for indoor sessions"
                    desc="Flag indoor lessons when blizzards, hurricanes, or tornadoes are active (US only)."
                    checked={p.weather_indoor_severe_alerts}
                    onChange={(v) => setP({ ...p, weather_indoor_severe_alerts: v })}
                  />
                  <ToggleRow
                    title="10-day heads-up digest"
                    desc="Daily 7am email listing flagged lessons in the next 10 days."
                    checked={p.weather_send_3day_digest}
                    onChange={(v) => setP({ ...p, weather_send_3day_digest: v })}
                  />
                  <ToggleRow
                    title="Day-before recap"
                    desc="Daily 6pm email recapping tomorrow's flagged lessons."
                    checked={p.weather_send_day_before}
                    onChange={(v) => setP({ ...p, weather_send_day_before: v })}
                  />

                  <div className="max-w-[20rem]">
                    <Label htmlFor="weather_default_sport">
                      Default sport (used when a location has no preset)
                    </Label>
                    <Select
                      value={p.weather_default_sport}
                      onValueChange={(v) =>
                        setP({ ...p, weather_default_sport: v as Profile["weather_default_sport"] })
                      }
                    >
                      <SelectTrigger id="weather_default_sport" className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pickleball">Pickleball</SelectItem>
                        <SelectItem value="tennis">Tennis</SelectItem>
                        <SelectItem value="golf">Golf</SelectItem>
                        <SelectItem value="generic">Generic outdoor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="precip_prob">Rain probability flag (%)</Label>
                      <Input
                        id="precip_prob"
                        type="number"
                        min={0}
                        max={100}
                        value={p.weather_threshold_precip_prob}
                        onChange={(e) =>
                          setP({
                            ...p,
                            weather_threshold_precip_prob: parseInt(e.target.value) || 0,
                          })
                        }
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precip_mm">Rain amount flag (mm)</Label>
                      <Input
                        id="precip_mm"
                        type="number"
                        min={0}
                        step={0.1}
                        value={p.weather_threshold_precip_mm}
                        onChange={(e) =>
                          setP({
                            ...p,
                            weather_threshold_precip_mm: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="wind_mph">Wind flag (mph)</Label>
                      <Input
                        id="wind_mph"
                        type="number"
                        min={0}
                        value={p.weather_threshold_wind_mph}
                        onChange={(e) =>
                          setP({ ...p, weather_threshold_wind_mph: parseInt(e.target.value) || 0 })
                        }
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="snow_cm">Snow flag (cm)</Label>
                      <Input
                        id="snow_cm"
                        type="number"
                        min={0}
                        step={0.1}
                        value={p.weather_threshold_snow_cm}
                        onChange={(e) =>
                          setP({ ...p, weather_threshold_snow_cm: parseFloat(e.target.value) || 0 })
                        }
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="temp_min">Cold flag (°F)</Label>
                      <Input
                        id="temp_min"
                        type="number"
                        value={p.weather_threshold_temp_min_f}
                        onChange={(e) =>
                          setP({
                            ...p,
                            weather_threshold_temp_min_f: parseInt(e.target.value) || 0,
                          })
                        }
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="temp_max">Hot flag (°F)</Label>
                      <Input
                        id="temp_max"
                        type="number"
                        value={p.weather_threshold_temp_max_f}
                        onChange={(e) =>
                          setP({
                            ...p,
                            weather_threshold_temp_max_f: parseInt(e.target.value) || 0,
                          })
                        }
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Sport presets in each location override these defaults when stricter.
                  </p>
                  <Button onClick={save} disabled={busy} className="w-full sm:w-auto">
                    {busy ? "Saving…" : "Save weather settings"}
                  </Button>
                </div>
              </SettingsSection>

              <SettingsSection
                id="storage"
                icon={HardDrive}
                title="Clip storage"
                description="Manage uploaded videos. Clips are auto-deleted 90 days after upload to keep CoachQuick fast and affordable."
              >
                <Button asChild className="w-full sm:w-auto">
                  <Link to="/app/settings/storage">Manage storage</Link>
                </Button>
              </SettingsSection>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ToggleRow({
  title,
  desc,
  checked,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[12px] border p-3.5">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
